/**
 * useNavigation - Navigation hook
 *
 * Wraps React Router's useNavigate and useLocation
 * to provide view-based navigation matching the bottom nav tabs.
 */

import { useNavigate, useLocation } from 'react-router-dom'
import { useCallback, useMemo } from 'react'

export type ViewId = 'generate' | 'review' | 'library' | 'stats' | 'settings'

const VIEW_PATHS: Record<ViewId, string> = {
  generate: '/',
  review: '/review',
  library: '/library',
  stats: '/stats',
  settings: '/settings',
}

const PATH_TO_VIEW: Record<string, ViewId> = {
  '/': 'generate',
  '': 'generate',
  '/review': 'review',
  '/library': 'library',
  '/stats': 'stats',
  '/settings': 'settings',
}

export interface UseNavigationReturn {
  currentView: ViewId
  navigateTo: (view: ViewId) => void
  isView: (view: ViewId) => boolean
}

export function useNavigation(): UseNavigationReturn {
  const navigate = useNavigate()
  const location = useLocation()

  const currentView = useMemo<ViewId>(() => {
    return PATH_TO_VIEW[location.pathname] ?? 'generate'
  }, [location.pathname])

  const navigateTo = useCallback((view: ViewId) => {
    navigate(VIEW_PATHS[view])
  }, [navigate])

  const isView = useCallback((view: ViewId) => {
    return currentView === view
  }, [currentView])

  return { currentView, navigateTo, isView }
}
